import React, { useState } from 'react';
import { Box, Button } from '@mui/material';
import { Plus } from 'lucide-react';

const BODY = '\'Mulish\', sans-serif';

export interface InlineSaveInputProps {
  /** Collapsed-button label and input placeholder, e.g. `Save "Toronto"`. */
  placeholder: string;
  /** Text on the pill save button, e.g. "Save". */
  saveLabel: string;
  /** Seeds the input when it expands so a suggested value is offered (still editable). */
  defaultValue?: string;
  /** Called with the trimmed, non-empty value when it is saved. */
  onSave: (value: string) => void;
}

/**
 * InlineSaveInput
 *
 * Atomic level: Molecule (dashed placeholder button that composes into a text
 * input + a pill save button).
 *
 * Collapsed, it is a dashed placeholder pill. Clicking it reveals a name input
 * with a "Save" pill beside it. Clicking away with an empty input reverts to the
 * button; Enter or the button commits a non-empty, trimmed value. Zero domain
 * knowledge — all content and the `onSave` callback come from props.
 */
export function InlineSaveInput({ placeholder, saveLabel, defaultValue = '', onSave }: InlineSaveInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');

  const open = () => {
    // Seed with the latest suggested value each time it opens (filters may have changed).
    setValue(defaultValue);
    setIsEditing(true);
  };

  const revertIfEmpty = () => {
    if (!value.trim()) setIsEditing(false);
  };

  const commit = () => {
    const next = value.trim();
    if (!next) return;
    onSave(next);
    setValue('');
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Button
        type="button"
        fullWidth
        disableRipple
        startIcon={<Plus size={16} />}
        onClick={open}
        sx={{
          justifyContent: 'flex-start',
          gap: 1,
          borderRadius: '100vmax',
          px: 2,
          py: 1.5,
          border: '1px dashed #d1d5db',
          color: '#6b7280',
          textTransform: 'none',
          fontFamily: BODY,
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: 1,
          '&:hover': {
            borderColor: '#0F1729',
            color: '#0F1729',
            backgroundColor: 'transparent',
          },
        }}
      >
        {placeholder}
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="input"
        autoFocus
        value={value}
        placeholder={placeholder}
        onChange={event => setValue(event.target.value)}
        onBlur={revertIfEmpty}
        onKeyDown={event => {
          if (event.key === 'Enter') commit();
          if (event.key === 'Escape') setIsEditing(false);
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          boxSizing: 'border-box',
          height: '3rem',
          px: '12px',
          fontFamily: BODY,
          fontSize: '0.875rem',
          lineHeight: 1.6,
          color: '#0F1729',
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '1rem',
          outline: 'none',
          '&:focus': { borderColor: '#000' },
        }}
      />
      <Button
        type="button"
        disableRipple
        // Keep input focus on mouse-down so the blur-revert never races the click.
        onMouseDown={event => event.preventDefault()}
        onClick={commit}
        sx={{
          flexShrink: 0,
          height: '3rem',
          px: 2.5,
          borderRadius: '100vmax',
          backgroundColor: '#000',
          color: '#fff',
          textTransform: 'none',
          fontFamily: BODY,
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: 1,
          '&:hover': { backgroundColor: '#111' },
        }}
      >
        {saveLabel}
      </Button>
    </Box>
  );
}

export default InlineSaveInput;
